
import { SystemLoadingSurface } from '@/components/ui/SystemLoading';
/**
 * LazyImage — 统一的图片懒加载组件
 *
 * 功能：
 * - shimmer 骨架屏占位
 * - opacity 渐入动画
 * - 加载失败回退图标
 * - 支持自定义 aspect ratio
 */

import { useState, useCallback, memo, useEffect, useMemo, useRef, type CSSProperties, type ImgHTMLAttributes, type SyntheticEvent } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import { isCosBucketUrl, toDisplayMediaUrl, toProxyMediaUrl, unwrapNextImageOptimizationUrl } from '@/lib/media-url';
import { getOptimizedImagePreviewUrl } from '@/lib/image-utils';
import { rememberDecodedMediaImage } from '@/lib/media-delivery';

const DEFAULT_MAX_IMAGE_RETRIES = 1;
const DEFAULT_RETRY_DELAY_MS = 600;
// Large source images may still be transferring after ten seconds. Keep a
// bounded recovery window without restarting a healthy download too early.
const DEFAULT_LOAD_TIMEOUT_MS = 30_000;
const LOADED_URL_CACHE_MAX = 1000;
const LAZY_IMAGE_ROOT_MARGIN = '360px 240px';

function toProxySrcSet(srcSet?: string): string | undefined {
  if (!srcSet) {
    return srcSet;
  }

  return srcSet
    .split(',')
    .map((candidate) => {
      const trimmed = candidate.trim();
      if (!trimmed) {
        return trimmed;
      }

      const [url, descriptor] = trimmed.split(/\s+/, 2);
      const displayUrl = toDisplayMediaUrl(url);
      return descriptor ? `${displayUrl} ${descriptor}` : displayUrl;
    })
    .join(', ');
}

function buildInlinePreviewSourceCandidates(url?: string | null, previewWidth?: number, preparedPreview = false): string[] {
  const displayUrl = toDisplayMediaUrl(url ?? '');
  if (!displayUrl) {
    return [];
  }
  if (preparedPreview) return [displayUrl];

  // The caller has already chosen the display size and cache key. Running the
  // optimizer again would replace that choice with 640px and try two expensive
  // variants serially before the original/proxy fallbacks supplied by callers.
  const originalUrl = unwrapNextImageOptimizationUrl(displayUrl);
  if (originalUrl !== displayUrl) {
    // Stale callers may wrap an authenticated proxy or signed URL. Keep the
    // shared optimizer's bypass rules before retaining a public cache key.
    const allowedPreview = getOptimizedImagePreviewUrl(originalUrl, previewWidth ?? 640, 75);
    return unwrapNextImageOptimizationUrl(allowedPreview) !== allowedPreview
      ? [displayUrl]
      : buildInlinePreviewSourceCandidates(originalUrl, previewWidth);
  }

  if (!isCosBucketUrl(displayUrl) || previewWidth !== undefined) {
    const optimizedUrl = getOptimizedImagePreviewUrl(displayUrl, previewWidth ?? 640, 75);
    return optimizedUrl && optimizedUrl !== displayUrl
      ? [optimizedUrl, displayUrl]
      : [displayUrl];
  }

  const proxyUrl = toProxyMediaUrl(displayUrl);
  return proxyUrl === displayUrl ? [displayUrl] : [displayUrl, proxyUrl];
}

/**
 * 全局 URL 缓存 — 图片 onLoad 后记住 URL，
 * 同一 URL 再次 mount 时直接跳过 shimmer。
 * 浏览器磁盘/内存缓存保证图片秒出。
 */
const _loadedUrls = new Set<string>();
function rememberLoadedUrl(loadKey: string) {
  if (_loadedUrls.has(loadKey)) {
    _loadedUrls.delete(loadKey);
  }
  _loadedUrls.add(loadKey);
  if (_loadedUrls.size > LOADED_URL_CACHE_MAX) {
    const oldest = _loadedUrls.values().next().value;
    if (oldest) _loadedUrls.delete(oldest);
  }
}

interface LazyImageProps {
  src: string | undefined | null;
  /** The caller is still resolving a source URL; an empty source is not a failure yet. */
  sourcePending?: boolean;
  fallbackSrcs?: Array<string | undefined | null>;
  srcSet?: string;
  sizes?: string;
  /** 请求与显示尺寸相称的缩略图；不改变引用/生成使用的原件地址。 */
  previewWidth?: number;
  /** A durable thumbnail already generated by the asset pipeline. */
  preparedPreview?: boolean;
  /** Use API-selected delivery sources verbatim, including legacy originals. */
  optimize?: boolean;
  alt?: string;
  /** 加载中 shimmer 的宽高比 class，如 "aspect-square" "aspect-[3/4]" "aspect-[4/3]" */
  aspectClass?: string;
  /** Loaded media defines its own bounds; aspectClass only reserves loading/error space. */
  naturalSize?: boolean;
  /** 图片 className（不含 opacity 相关，组件自动处理） */
  imgClassName?: string;
  /** 错误回退图标大小 class，默认 "h-6 w-6" */
  fallbackIconClass?: string;
  /** 错误回退自定义 React 节点（替代默认图标） */
  fallbackNode?: React.ReactNode;
  /** 外层 wrapper 额外 className */
  className?: string;
  /** 外层 wrapper 行内样式（如动态 aspectRatio） */
  wrapperStyle?: CSSProperties;
  /** img 原生 loading 属性，默认 "lazy" */
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  referrerPolicy?: ImgHTMLAttributes<HTMLImageElement>['referrerPolicy'];
  retryCount?: number;
  retryDelayMs?: number;
  loadTimeoutMs?: number;
  onClick?: (e: React.MouseEvent) => void;
  onLoad?: (event: SyntheticEvent<HTMLImageElement>) => void;
  onError?: () => void;
  /** 叠加在图片上方的内容（如 hover overlay） */
  children?: React.ReactNode;
}

export const LazyImage = memo(function LazyImage({
  src,
  sourcePending = false,
  fallbackSrcs,
  srcSet,
  sizes,
  previewWidth,
  preparedPreview = false,
  optimize = true,
  alt = '',
  aspectClass = 'aspect-square',
  naturalSize = false,
  imgClassName = 'h-full w-full object-cover',
  fallbackIconClass = 'h-6 w-6',
  fallbackNode,
  className = '',
  wrapperStyle,
  loading = 'lazy',
  fetchPriority = 'auto',
  referrerPolicy,
  retryCount = DEFAULT_MAX_IMAGE_RETRIES,
  retryDelayMs = DEFAULT_RETRY_DELAY_MS,
  loadTimeoutMs = DEFAULT_LOAD_TIMEOUT_MS,
  onClick,
  onLoad,
  onError,
  children,
}: LazyImageProps) {
  const sourceList = useMemo(() => {
    const seen = new Set<string>();
    return [src, ...(fallbackSrcs ?? [])]
      .flatMap((candidate) => buildInlinePreviewSourceCandidates(candidate, previewWidth, !optimize || (preparedPreview && candidate === src)))
      .filter((candidate): candidate is string => {
        if (!candidate || seen.has(candidate)) return false;
        seen.add(candidate);
        return true;
      });
  }, [fallbackSrcs, optimize, previewWidth, preparedPreview, src]);
  const sourceSignature = sourceList.join('\u0000');
  const resolvedSrcSet = useMemo(() => toProxySrcSet(srcSet), [srcSet]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const onErrorRef = useRef(onError);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [sourceIndex, setSourceIndex] = useState(0);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const resolvedSrc = sourceList[sourceIndex] ?? '';
  const loadKey = resolvedSrc ? `${resolvedSrc}__${resolvedSrcSet || ''}__${sizes || ''}` : '';
  const alreadyCached = !!(loadKey && _loadedUrls.has(loadKey));
  const [shouldRequestImage, setShouldRequestImage] = useState(
    loading === 'eager' || alreadyCached,
  );
  const [imgLoaded, setImgLoaded] = useState(alreadyCached);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (imgError) onErrorRef.current?.();
  }, [imgError]);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearRetryTimer(), [clearRetryTimer]);

  useEffect(() => {
    if (loading === 'eager' || alreadyCached) {
      setShouldRequestImage(true);
      return;
    }

    const wrapper = wrapperRef.current;
    if (!wrapper || typeof IntersectionObserver === 'undefined') {
      setShouldRequestImage(true);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      setShouldRequestImage(true);
      observer.disconnect();
    }, {
      // Native loading="lazy" uses a browser-defined distance that can be very
      // large. Gate src ourselves so virtualized chat overscan and horizontal
      // result rails do not start dozens of off-screen requests at once.
      rootMargin: LAZY_IMAGE_ROOT_MARGIN,
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [alreadyCached, loading, sourceSignature]);

  useEffect(() => {
    clearRetryTimer();
    setSourceIndex(0);
    setRetryAttempt(0);
    setImgLoaded(false);
    setImgError(false);
  }, [clearRetryTimer, sourceSignature]);

  useEffect(() => {
    setImgLoaded(!!(loadKey && _loadedUrls.has(loadKey)));
    setImgError(false);
  }, [loadKey, retryAttempt]);

  const handleFailure = useCallback(() => {
    if (!resolvedSrc) return;
    clearRetryTimer();
    setImgLoaded(false);

    // 优化缩略图失败时立即切原图，不要先在同一个失效 URL 上等待第二轮超时。
    if (sourceIndex < sourceList.length - 1) {
      setSourceIndex((index) => index + 1);
      setRetryAttempt(0);
      setImgError(false);
      return;
    }

    if (retryAttempt < retryCount) {
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        setRetryAttempt((attempt) => attempt + 1);
      }, retryDelayMs * (retryAttempt + 1));
      return;
    }

    setImgError(true);
  }, [clearRetryTimer, resolvedSrc, retryAttempt, retryCount, retryDelayMs, sourceIndex, sourceList.length]);

  useEffect(() => {
    const img = imgRef.current;
    if (!shouldRequestImage || !img || !img.complete) {
      return;
    }

    if (img.naturalWidth > 0) {
      if (loadKey) {
        rememberLoadedUrl(loadKey);
        rememberDecodedMediaImage(img.currentSrc || img.src);
      }
      setImgLoaded(true);
      setImgError(false);
      return;
    }

    handleFailure();
  }, [handleFailure, loadKey, resolvedSrc, retryAttempt, shouldRequestImage]);

  useEffect(() => {
    if (!shouldRequestImage || !resolvedSrc || imgLoaded || imgError || loadTimeoutMs <= 0) {
      return;
    }

    const timeoutId = setTimeout(handleFailure, loadTimeoutMs);
    return () => clearTimeout(timeoutId);
  }, [handleFailure, imgError, imgLoaded, loadTimeoutMs, resolvedSrc, retryAttempt, shouldRequestImage]);

  const handleLoad = useCallback((event: SyntheticEvent<HTMLImageElement>) => {
    if (loadKey) rememberLoadedUrl(loadKey);
    rememberDecodedMediaImage(event.currentTarget.currentSrc || event.currentTarget.src);
    clearRetryTimer();
    setImgLoaded(true);
    setImgError(false);
    onLoad?.(event);
  }, [clearRetryTimer, loadKey, resolvedSrc, onLoad]);
  const handleError = useCallback(() => handleFailure(), [handleFailure]);

  if ((!resolvedSrc && !sourcePending) || imgError) {
    return (
      <div
        className={`flex ${aspectClass} w-full items-center justify-center bg-gray-50 ${className}`}
        style={wrapperStyle}
      >
        {fallbackNode === undefined
          ? <ImageIcon className={`${fallbackIconClass} text-gray-200`} />
          : fallbackNode}
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className={`relative ${naturalSize && imgLoaded ? '' : aspectClass} ${className}`}
      style={wrapperStyle}
      onClick={onClick}
      aria-busy={sourcePending || !imgLoaded}
    >
      {!imgLoaded && (
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden bg-[#EDF7FB]">
          <SystemLoadingSurface className="absolute inset-0       motion-reduce:animate-none" />
          {fallbackNode !== null ? (
            <ImageIcon className={`${fallbackIconClass} relative text-[#4ABDE9]/25`} aria-hidden="true" />
          ) : null}
        </div>
      )}
      {shouldRequestImage && resolvedSrc ? (
        <img
          key={`${resolvedSrc}::${retryAttempt}`}
          ref={imgRef}
          src={resolvedSrc}
          srcSet={resolvedSrcSet}
          sizes={sizes}
          alt={alt}
          className={`${imgClassName} transition-opacity duration-300 motion-reduce:transition-none ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={handleLoad}
          onError={handleError}
          loading={loading}
          fetchPriority={fetchPriority}
          referrerPolicy={referrerPolicy}
          decoding="async"
          draggable={false}
          data-pin-nopin="true"
          nopin="nopin"
        />
      ) : null}
      {children}
    </div>
  );
});
